/**
 * QuizReviewAdminPanel — admin/manager Quiz Review & Approval MVP (Phase Q3).
 *
 * The go-live gate for the Training Quiz feature: a manager reviews the draft
 * quizzes (questions + correct answers + explanations), then explicitly approves
 * the good ones. Approval is the ONLY thing that makes a quiz eligible to appear
 * to technicians, and only where the `trainingQuizMvp` flag is enabled.
 *
 * INTEGRITY / SAFETY:
 *  - This panel NEVER enables trainingQuizMvp and shows NO technician quiz UI.
 *  - It never mutates completion_status / completion_confidence.
 *  - Every approval requires an explicit confirmation. No "approve all visible".
 *  - Transcript text is never displayed here (only generated questions).
 *  - Approving a quiz is a KNOWLEDGE signal, not watch/Companion/provider
 *    verification — surfaced in the confirmation copy.
 *
 * Mounted as the "Quiz Review" tab in TrainingCatalogAdmin, inheriting its
 * InternalAdminRoute + useIsInternalAdmin gates.
 */
import { useMemo, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, CheckCircle2, Archive, Wand2, ShieldCheck } from 'lucide-react';
import {
  useQuizReviewList,
  type QuizReviewRow,
  type QuizStatus,
} from '@/hooks/useQuizReviewList';

type StatusFilter = 'all' | QuizStatus;
type ConfirmKind = 'approve' | 'archive' | 'regenerate';

const SIGNAL_COPY = 'Quiz approval is a knowledge signal, not Companion/provider watch verification.';
const AVAILABILITY_COPY =
  'Approved quizzes become available only where the training quiz feature is enabled.';

function StatusBadge({ status }: { status: QuizStatus }) {
  const variant = status === 'approved' ? 'default' : status === 'archived' ? 'secondary' : 'outline';
  return (
    <Badge variant={variant} data-testid={`quiz-review-status-badge-${status}`}>
      {status}
    </Badge>
  );
}

function shortHash(h: string | null): string {
  return h ? `${h.slice(0, 8)}…` : 'missing';
}

export function QuizReviewAdminPanel() {
  const { rows, isLoading, error, refresh, approve, approveMany, archive, regenerate } =
    useQuizReviewList();

  // filters
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('draft');
  const [providerFilter, setProviderFilter] = useState('');
  const [titleFilter, setTitleFilter] = useState('');
  const [minQ, setMinQ] = useState('');

  // selection (draft rows only)
  const [selected, setSelected] = useState<Set<string>>(new Set());

  // detail + confirmations
  const [detailRow, setDetailRow] = useState<QuizReviewRow | null>(null);
  const [confirm, setConfirm] = useState<{ kind: ConfirmKind; row: QuizReviewRow } | null>(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [sampleAudited, setSampleAudited] = useState(false);
  const [busy, setBusy] = useState(false);
  const [statusMsg, setStatusMsg] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const minQn = Number.parseInt(minQ, 10);
    const prov = providerFilter.trim().toLowerCase();
    const title = titleFilter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (prov && !(r.providerName ?? '').toLowerCase().includes(prov)) return false;
      if (title && !r.title.toLowerCase().includes(title)) return false;
      if (!Number.isNaN(minQn) && r.questionCount < minQn) return false;
      return true;
    });
  }, [rows, statusFilter, providerFilter, titleFilter, minQ]);

  const selectableDraftIds = useMemo(
    () => filtered.filter((r) => r.status === 'draft').map((r) => r.id),
    [filtered],
  );
  const selectedList = useMemo(
    () => [...selected].filter((id) => selectableDraftIds.includes(id)),
    [selected, selectableDraftIds],
  );

  const allDraftsSelected =
    selectableDraftIds.length > 0 && selectableDraftIds.every((id) => selected.has(id));

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleAll = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (selectableDraftIds.every((id) => next.has(id))) {
        selectableDraftIds.forEach((id) => next.delete(id));
      } else {
        selectableDraftIds.forEach((id) => next.add(id));
      }
      return next;
    });

  async function runAction() {
    if (!confirm) return;
    const { kind, row } = confirm;
    setBusy(true);
    setStatusMsg(null);
    let res: { ok: boolean; error: string | null };
    if (kind === 'approve') res = await approve(row.id);
    else if (kind === 'archive') res = await archive(row.id);
    else res = await regenerate(row.artifactId);
    setBusy(false);
    setConfirm(null);
    if (res.ok) {
      setStatusMsg(
        kind === 'approve'
          ? `Approved “${row.title}”. It is now the approved quiz for that artifact.`
          : kind === 'archive'
            ? `Archived the draft for “${row.title}”.`
            : `Regenerated a fresh draft for “${row.title}”.`,
      );
      setDetailRow(null);
      setSelected(new Set());
      await refresh();
    } else {
      setStatusMsg(`Action failed: ${res.error}`);
    }
  }

  async function runBulkApprove() {
    setBusy(true);
    setStatusMsg(null);
    const { approved, failed } = await approveMany(selectedList);
    setBusy(false);
    setBulkConfirm(false);
    setSelected(new Set());
    setStatusMsg(
      `Approved ${approved.length} quiz${approved.length === 1 ? '' : 'zes'}` +
        (failed.length ? `; ${failed.length} failed.` : '.'),
    );
    await refresh();
  }

  return (
    <Card data-testid="quiz-review-admin-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldCheck className="h-5 w-5" />
          Quiz Review &amp; Approval
        </CardTitle>
        <CardDescription data-testid="quiz-review-safety-copy" className="space-y-1">
          <span className="block">{SIGNAL_COPY}</span>
          <span className="block">{AVAILABILITY_COPY}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Filters */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex gap-1" data-testid="quiz-review-filter-status">
            {(['all', 'draft', 'approved', 'archived'] as StatusFilter[]).map((s) => (
              <Button
                key={s}
                type="button"
                size="sm"
                variant={statusFilter === s ? 'default' : 'outline'}
                onClick={() => setStatusFilter(s)}
                data-testid={`quiz-review-filter-status-${s}`}
              >
                {s}
              </Button>
            ))}
          </div>
          <Input
            className="w-44"
            placeholder="Filter by provider"
            value={providerFilter}
            onChange={(e) => setProviderFilter(e.target.value)}
            data-testid="quiz-review-filter-provider"
          />
          <Input
            className="w-52"
            placeholder="Search artifact title"
            value={titleFilter}
            onChange={(e) => setTitleFilter(e.target.value)}
            data-testid="quiz-review-filter-title"
          />
          <Input
            type="number"
            min={0}
            className="w-32"
            placeholder="Min questions"
            value={minQ}
            onChange={(e) => setMinQ(e.target.value)}
            data-testid="quiz-review-filter-qcount"
          />
          <Button
            type="button"
            size="sm"
            disabled={selectedList.length === 0 || busy}
            onClick={() => setBulkConfirm(true)}
            data-testid="quiz-review-approve-selected"
          >
            Approve selected ({selectedList.length})
          </Button>
        </div>

        {statusMsg && (
          <p className="text-sm text-muted-foreground" data-testid="quiz-review-action-status">
            {statusMsg}
          </p>
        )}

        {isLoading ? (
          <p className="text-sm text-muted-foreground" data-testid="quiz-review-loading">
            <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            Loading quizzes…
          </p>
        ) : error ? (
          <p className="text-sm text-destructive" data-testid="quiz-review-error">
            {error}
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="quiz-review-empty">
            No quizzes match the current filters.
          </p>
        ) : (
          <div className="overflow-x-auto rounded border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8">
                    {selectableDraftIds.length > 0 && (
                      <input
                        type="checkbox"
                        aria-label="Select all filtered drafts"
                        checked={allDraftsSelected}
                        onChange={toggleAll}
                        data-testid="quiz-review-select-all"
                      />
                    )}
                  </TableHead>
                  <TableHead>Artifact</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Generated</TableHead>
                  <TableHead className="text-center">Qs</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Source hash</TableHead>
                  <TableHead>Stale</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((r) => (
                  <TableRow key={r.id} data-testid={`quiz-review-row-${r.id}`}>
                    <TableCell>
                      {r.status === 'draft' && (
                        <input
                          type="checkbox"
                          aria-label={`Select ${r.title}`}
                          checked={selected.has(r.id)}
                          onChange={() => toggle(r.id)}
                          data-testid={`quiz-review-select-${r.id}`}
                        />
                      )}
                    </TableCell>
                    <TableCell className="max-w-[18rem] truncate font-medium">{r.title}</TableCell>
                    <TableCell>{r.providerName || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {r.generatedAt ? r.generatedAt.slice(0, 10) : '—'}
                    </TableCell>
                    <TableCell className="text-center">{r.questionCount}</TableCell>
                    <TableCell>
                      <StatusBadge status={r.status} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{shortHash(r.sourceTranscriptHash)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.stale === 'unknown' ? '—' : r.stale}
                    </TableCell>
                    <TableCell>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => setDetailRow(r)}
                        data-testid={`quiz-review-open-${r.id}`}
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Detail / review dialog */}
      <Dialog open={!!detailRow} onOpenChange={(o) => !o && setDetailRow(null)}>
        <DialogContent className="max-w-2xl" data-testid="quiz-review-detail">
          {detailRow && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {detailRow.title} <StatusBadge status={detailRow.status} />
                </DialogTitle>
                <DialogDescription>
                  {detailRow.providerName || 'Unknown provider'} · {detailRow.questionCount} questions ·{' '}
                  {SIGNAL_COPY}
                </DialogDescription>
              </DialogHeader>

              <div className="max-h-[50vh] space-y-3 overflow-y-auto pr-1">
                {detailRow.questions.map((q, i) => (
                  <div key={i} className="rounded border p-2 text-sm" data-testid={`quiz-review-detail-q-${i}`}>
                    <p className="font-medium">
                      {i + 1}. {q.question}
                    </p>
                    <ul className="ml-4 list-disc">
                      {q.choices.map((c, ci) => (
                        <li key={ci} className={ci === q.correct_index ? 'font-medium text-green-600' : ''}>
                          {c}
                          {ci === q.correct_index ? ' ✓ correct' : ''}
                        </li>
                      ))}
                    </ul>
                    <p className="text-muted-foreground">Why: {q.explanation}</p>
                  </div>
                ))}
              </div>

              <DialogFooter className="flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy}
                  onClick={() => setConfirm({ kind: 'regenerate', row: detailRow })}
                  data-testid="quiz-review-regenerate"
                >
                  <Wand2 className="mr-1 h-4 w-4" /> Regenerate
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={busy || detailRow.status === 'archived'}
                  onClick={() => setConfirm({ kind: 'archive', row: detailRow })}
                  data-testid="quiz-review-archive"
                >
                  <Archive className="mr-1 h-4 w-4" /> Archive / reject
                </Button>
                <Button
                  type="button"
                  disabled={busy || detailRow.status === 'approved'}
                  onClick={() => setConfirm({ kind: 'approve', row: detailRow })}
                  data-testid="quiz-review-approve"
                >
                  <CheckCircle2 className="mr-1 h-4 w-4" /> Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Per-quiz confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent data-testid="quiz-review-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === 'approve'
                ? 'Approve this quiz?'
                : confirm?.kind === 'archive'
                  ? 'Archive / reject this draft?'
                  : 'Regenerate this draft with AI?'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === 'approve' && (
                <>
                  {SIGNAL_COPY} {AVAILABILITY_COPY} Any previously approved quiz for this artifact is
                  archived.
                </>
              )}
              {confirm?.kind === 'archive' &&
                'The draft will be hidden from review and never shown to technicians.'}
              {confirm?.kind === 'regenerate' &&
                'This makes one AI call to generate a fresh draft (it may cost money). The result stays a hidden draft until approved.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quiz-review-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void runAction();
              }}
              data-testid="quiz-review-confirm-ok"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk approve confirmation — with a sample-audit gate. */}
      <AlertDialog
        open={bulkConfirm}
        onOpenChange={(o) => {
          setBulkConfirm(o);
          if (!o) setSampleAudited(false);
        }}
      >
        <AlertDialogContent data-testid="quiz-review-approve-selected-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Approve {selectedList.length} selected drafts?</AlertDialogTitle>
            <AlertDialogDescription>
              Each is approved individually via the audited approval RPC. {SIGNAL_COPY}{' '}
              {AVAILABILITY_COPY}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <label
            className="flex items-start gap-2 text-sm"
            data-testid="quiz-review-sample-audit-label"
          >
            <input
              type="checkbox"
              className="mt-1"
              checked={sampleAudited}
              onChange={(e) => setSampleAudited(e.target.checked)}
              data-testid="quiz-review-sample-audit-ack"
            />
            <span>I have reviewed a representative sample of these drafts and confirm they are fit to approve.</span>
          </label>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="quiz-review-approve-selected-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={!sampleAudited || busy}
              onClick={(e) => {
                e.preventDefault();
                void runBulkApprove();
              }}
              data-testid="quiz-review-approve-selected-confirm"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Approve all selected'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
